import type { MatchInfo, MatchSnapshot } from "@/lib/matches";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { getMatchRoster, type RosterPlayer } from "@/lib/sports/matchRosters";

/** Country/team full names by ISO/team code. Used so visible UI never shows raw codes. */
const TEAM_FULL_NAMES: Record<string, string> = {
  MEX: PREVIEW_MODE ? "Spain" : "Mexico", ARG: "Argentina", BRA: "Brazil", GER: "Germany",
  ESP: "Spain", FRA: "France", USA: "United States", COL: "Colombia",
  ENG: "England", POR: "Portugal",
};

export function getTeamFullName(code: string): string {
  return TEAM_FULL_NAMES[code] ?? code;
}

/**
 * Central, single source of truth for the currently active match context.
 * Every visible surface (Header, Radar, Stats, Commentary, AI, Halftime Hub,
 * Notifications) must consume this helper instead of hardcoding MEX/ARG.
 */
export interface ActiveMatchContext {
  id: string;
  homeCode: string;
  awayCode: string;
  homeTeamName: string;
  awayTeamName: string;
  homeFlag: string;
  awayFlag: string;
  homeColor: string;
  awayColor: string;
  homePlayers: RosterPlayer[];
  awayPlayers: RosterPlayer[];
  /** Heuristic top performer for the active match (typically a forward). */
  topPerformer: { player: RosterPlayer; teamCode: string; teamName: string };
  homeScore: number;
  awayScore: number;
  minute: number;
  status: MatchSnapshot["status"];
  competition: string;
  /** Compact one-line label safe to feed into the AI prompt or notifications. */
  label: string;
}

export function getActiveMatchContext(
  match: MatchInfo,
  snapshot: MatchSnapshot | undefined,
  overrides?: { homeScore?: number; awayScore?: number; minute?: number },
): ActiveMatchContext {
  const homeScore = overrides?.homeScore ?? snapshot?.homeScore ?? 0;
  const awayScore = overrides?.awayScore ?? snapshot?.awayScore ?? 0;
  const minute = overrides?.minute ?? snapshot?.minute ?? 0;
  const roster = getMatchRoster(match.id);
  const homeTeamName = getTeamFullName(match.homeCode);
  const awayTeamName = getTeamFullName(match.awayCode);
  // Pick a stable "top performer": leading attacker on the team currently leading,
  // or the home #10-ish forward by default. This keeps the UI team-neutral.
  const leadingHome = homeScore >= awayScore;
  const pool = leadingHome ? roster.homePlayers : roster.awayPlayers;
  const teamCode = leadingHome ? match.homeCode : match.awayCode;
  const teamName = leadingHome ? homeTeamName : awayTeamName;
  const topPlayer = pool.find((p) => p.position === "FWD") ?? pool[pool.length - 1];
  return {
    id: match.id,
    homeCode: match.homeCode,
    awayCode: match.awayCode,
    homeTeamName,
    awayTeamName,
    homeFlag: match.homeFlag,
    awayFlag: match.awayFlag,
    homeColor: match.homeColor,
    awayColor: match.awayColor,
    homePlayers: roster.homePlayers,
    awayPlayers: roster.awayPlayers,
    topPerformer: { player: topPlayer, teamCode, teamName },
    homeScore,
    awayScore,
    minute,
    status: snapshot?.status ?? "LIVE",
    competition: "FIFA World Cup 2026",
    label: `${match.homeCode} ${homeScore} - ${awayScore} ${match.awayCode} · min ${minute}`,
  };
}

export interface RichMatchContext {
  formationHome: string;
  formationAway: string;
  starters: { home: RosterPlayer[]; away: RosterPlayer[] };
  bench: { home: RosterPlayer[]; away: RosterPlayer[] };
  injured: { home: RosterPlayer[]; away: RosterPlayer[] };
  suspended: { home: RosterPlayer[]; away: RosterPlayer[] };
  yellowCards: Array<{ team: "home" | "away"; player: RosterPlayer; minute: number }>;
  redCards: Array<{ team: "home" | "away"; player: RosterPlayer; minute: number }>;
  goals: Array<{ team: "home" | "away"; player: RosterPlayer; minute: number }>;
  assists: Array<{ team: "home" | "away"; player: RosterPlayer; minute: number }>;
  substitutions: Array<{ team: "home" | "away"; out: RosterPlayer; in: RosterPlayer; minute: number }>;
  addedTime: number;
  xg: { home: number; away: number };
  shots: { home: number; away: number };
  possession: { home: number; away: number };
  pressure: { home: number; away: number };
  fatigue: { home: number; away: number };
  h2hSummary: string;
  last5: { home: string; away: string };
  tacticalRisks: string[];
}

/**
 * Deterministic-but-rich match context. Values are derived from the active
 * match id, score, and minute so the AI can answer "who has a yellow card?",
 * "who is injured?", "who is at risk of being sent off?" without any extra
 * data source. Mark as DEMO; once a real provider is wired this becomes
 * the formatter, not the data source.
 */
export function buildRichMatchContext(
  match: MatchInfo,
  snapshot: MatchSnapshot | undefined,
  base: ActiveMatchContext,
): RichMatchContext {
  const roster = getMatchRoster(match.id);
  const minute = base.minute;
  const hashCode = match.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const homePlayers = roster.homePlayers;
  const awayPlayers = roster.awayPlayers;
  const pickPlayer = (pool: RosterPlayer[], salt: number) =>
    pool[(hashCode + salt) % pool.length];

  // Cards — yellow at ~min 18 home / 34 away; second yellow risk if minute > 70
  const yellowCards = [
    { team: "home" as const, player: pickPlayer(homePlayers.filter(p => p.position !== "GK"), 1), minute: Math.min(minute, 18) },
    { team: "away" as const, player: pickPlayer(awayPlayers.filter(p => p.position === "MID"), 2), minute: Math.min(minute, 34) },
  ].filter(c => minute >= c.minute);
  const redCards: RichMatchContext["redCards"] = minute > 80 && (hashCode % 5 === 0)
    ? [{ team: "away", player: pickPlayer(awayPlayers, 3), minute: 78 }]
    : [];

  // Injuries / suspensions — picked deterministically per match id
  const injured = {
    home: [pickPlayer(homePlayers, 7)],
    away: [pickPlayer(awayPlayers, 9)],
  };
  const suspended = {
    home: hashCode % 3 === 0 ? [pickPlayer(homePlayers, 5)] : [],
    away: hashCode % 4 === 0 ? [pickPlayer(awayPlayers, 6)] : [],
  };

  // Goals & assists tied to current score
  const goals: RichMatchContext["goals"] = [];
  for (let i = 0; i < base.homeScore; i++) {
    goals.push({ team: "home", player: pickPlayer(homePlayers.filter(p => p.position === "FWD"), i), minute: 12 + i * 23 });
  }
  for (let i = 0; i < base.awayScore; i++) {
    goals.push({ team: "away", player: pickPlayer(awayPlayers.filter(p => p.position === "FWD"), i + 1), minute: 18 + i * 19 });
  }
  const assists = goals.map((g, i) => ({
    team: g.team,
    player: pickPlayer(g.team === "home" ? homePlayers.filter(p => p.position === "MID") : awayPlayers.filter(p => p.position === "MID"), i),
    minute: g.minute,
  }));

  // Substitutions — appear after 60'
  const substitutions: RichMatchContext["substitutions"] = minute >= 60
    ? [{ team: "home", out: pickPlayer(homePlayers.filter(p => p.position === "MID"), 4), in: pickPlayer(homePlayers, 8), minute: 62 }]
    : [];
  if (minute >= 75) {
    substitutions.push({ team: "away", out: pickPlayer(awayPlayers.filter(p => p.position === "FWD"), 5), in: pickPlayer(awayPlayers, 7), minute: 74 });
  }

  const possessionHome = 48 + ((hashCode + minute) % 11);
  const xgHome = +(0.6 + base.homeScore * 0.7 + (minute / 90) * 0.5).toFixed(2);
  const xgAway = +(0.4 + base.awayScore * 0.6 + (minute / 90) * 0.4).toFixed(2);
  const shotsHome = 4 + base.homeScore * 2 + Math.floor(minute / 18);
  const shotsAway = 3 + base.awayScore * 2 + Math.floor(minute / 22);
  const fatigueHome = Math.min(95, 30 + Math.floor(minute * 0.7));
  const fatigueAway = Math.min(95, 28 + Math.floor(minute * 0.72));
  const pressureHome = 50 + ((hashCode + minute * 2) % 25);

  const tacticalRisks: string[] = [];
  if (yellowCards.length >= 2 && minute > 60) tacticalRisks.push("multiple_bookings");
  if (fatigueHome > 80 || fatigueAway > 80) tacticalRisks.push("fatigue_high");
  if (Math.abs(base.homeScore - base.awayScore) <= 1 && minute > 75) tacticalRisks.push("late_game_swing");
  if (redCards.length) tacticalRisks.push("numerical_disadvantage");

  const formationHome = "4-3-3";
  const formationAway = hashCode % 2 === 0 ? "4-4-2" : "4-2-3-1";
  const last5Home = ["W", "W", "D", "L", "W"].join("-");
  const last5Away = ["D", "W", "W", "L", "D"].join("-");

  return {
    formationHome,
    formationAway,
    starters: { home: homePlayers, away: awayPlayers },
    bench: {
      home: [pickPlayer(homePlayers, 2), pickPlayer(homePlayers, 4)],
      away: [pickPlayer(awayPlayers, 3), pickPlayer(awayPlayers, 5)],
    },
    injured,
    suspended,
    yellowCards,
    redCards,
    goals,
    assists,
    substitutions,
    addedTime: minute > 88 ? 4 : minute > 43 && minute < 50 ? 2 : 0,
    xg: { home: xgHome, away: xgAway },
    shots: { home: shotsHome, away: shotsAway },
    possession: { home: possessionHome, away: 100 - possessionHome },
    pressure: { home: pressureHome, away: 100 - pressureHome },
    fatigue: { home: fatigueHome, away: fatigueAway },
    h2hSummary: `${base.homeTeamName} 4W - 3D - 3L vs ${base.awayTeamName} (last 10)`,
    last5: { home: last5Home, away: last5Away },
    tacticalRisks,
  };
  // snapshot reserved for future enrichments (e.g. recentEvent type)
  void snapshot;
}

/** Format the rich context as a single multi-line prompt block for the AI. */
export function formatRichContextForAi(base: ActiveMatchContext, rich: RichMatchContext): string {
  const fmtPlayer = (p: RosterPlayer) => `${p.name} (#${p.number} ${p.position})`;
  const fmtCards = (cards: RichMatchContext["yellowCards"]) =>
    cards.length
      ? cards.map(c => `${c.team === "home" ? base.homeTeamName : base.awayTeamName}: ${fmtPlayer(c.player)} @${c.minute}'`).join("; ")
      : "none";
  const fmtList = (xs: RosterPlayer[]) => (xs.length ? xs.map(fmtPlayer).join(", ") : "none");
  return [
    `Match: ${base.homeTeamName} (${base.homeCode}) ${base.homeScore} - ${base.awayScore} ${base.awayTeamName} (${base.awayCode})`,
    `Minute: ${base.minute}' (+${rich.addedTime} added) · Status: ${base.status} · Competition: ${base.competition}`,
    `Formation: ${base.homeCode} ${rich.formationHome} vs ${base.awayCode} ${rich.formationAway}`,
    `Starters ${base.homeCode}: ${rich.starters.home.map(fmtPlayer).join(", ")}`,
    `Starters ${base.awayCode}: ${rich.starters.away.map(fmtPlayer).join(", ")}`,
    `Bench: ${base.homeCode}: ${fmtList(rich.bench.home)} | ${base.awayCode}: ${fmtList(rich.bench.away)}`,
    `Injured: ${base.homeCode}: ${fmtList(rich.injured.home)} | ${base.awayCode}: ${fmtList(rich.injured.away)}`,
    `Suspended: ${base.homeCode}: ${fmtList(rich.suspended.home)} | ${base.awayCode}: ${fmtList(rich.suspended.away)}`,
    `Yellow cards: ${fmtCards(rich.yellowCards)}`,
    `Red cards: ${fmtCards(rich.redCards)}`,
    `Goals: ${rich.goals.length ? rich.goals.map(g => `${fmtPlayer(g.player)} @${g.minute}'`).join("; ") : "none"}`,
    `Assists: ${rich.assists.length ? rich.assists.map(a => `${fmtPlayer(a.player)} @${a.minute}'`).join("; ") : "none"}`,
    `Substitutions: ${rich.substitutions.length ? rich.substitutions.map(s => `${s.team === "home" ? base.homeCode : base.awayCode}: ${fmtPlayer(s.out)} → ${fmtPlayer(s.in)} @${s.minute}'`).join("; ") : "none"}`,
    `Top performer: ${base.topPerformer.player.name} (${base.topPerformer.teamName})`,
    `xG: ${base.homeCode} ${rich.xg.home} - ${rich.xg.away} ${base.awayCode}`,
    `Shots: ${base.homeCode} ${rich.shots.home} - ${rich.shots.away} ${base.awayCode}`,
    `Possession: ${base.homeCode} ${rich.possession.home}% - ${rich.possession.away}% ${base.awayCode}`,
    `Pressure: ${base.homeCode} ${rich.pressure.home} - ${rich.pressure.away} ${base.awayCode}`,
    `Fatigue: ${base.homeCode} ${rich.fatigue.home} - ${rich.fatigue.away} ${base.awayCode}`,
    `H2H: ${rich.h2hSummary}`,
    `Last 5 form: ${base.homeCode} ${rich.last5.home} | ${base.awayCode} ${rich.last5.away}`,
    `Tactical risks: ${rich.tacticalRisks.length ? rich.tacticalRisks.join(", ") : "none"}`,
  ].join("\n");
}